import React from 'react';
import { Typography } from 'antd';
import {
  CommentOutlined,
  EditOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import { parseISO as _parseISO, format, isValid as _isValid } from 'date-fns';
import { withStyles } from '@material-ui/core/styles';

const { Text } = Typography;

const FIELD_LABELS = {
  expense_type: 'Тип',
  expense_stage: 'Этап',
  amount: 'Сумма',
  author: 'Автор',
  description: 'Описание',
  comment: 'Комментарий',
  name: 'Название',
  status: 'Статус',
  expense_purpose: 'Статья расхода',
  expense_company: 'Компания',
  pay_type: 'Расчет',
  date_payment: 'Дата платежа',
  plan_date_payment: 'Оплатить до',
  flow_rate: 'OPEX / CAPEX',
  counterparty: 'Контрагент',
};

const DATE_FIELDS = ['date_payment', 'plan_date_payment'];

const formatEventDate = (createdAt) => {
  if (!createdAt) return '';
  return format(_parseISO(createdAt), 'dd.MM.yyyy HH:mm');
};

const formatFieldValue = (key, value) => {
  if (value === null || value === undefined || value === '') return '—';

  if (key === 'amount') {
    const number = Number(value);
    if (!Number.isNaN(number)) {
      return `${number.toLocaleString('ru-RU')} ₽`;
    }
  }

  if (DATE_FIELDS.includes(key) && typeof value === 'string') {
    const date = _parseISO(value);
    if (_isValid(date)) {
      return format(date, 'dd.MM.yyyy HH:mm');
    }
  }

  return String(value);
};

const getEventMeta = (event) => {
  const isCommentOnly = Object.keys(event.changes || {}).length === 1 && event.changes.comment;

  if (isCommentOnly) {
    return {
      title: 'Комментарий',
      icon: <CommentOutlined />,
      variant: 'comment',
    };
  }

  if (event.event === 'create') {
    return {
      title: 'Создание расхода',
      icon: <PlusCircleOutlined />,
      variant: 'create',
    };
  }

  return {
    title: 'Изменение',
    icon: <EditOutlined />,
    variant: 'update',
  };
};

const ExpenseEventsList = ({ events = [], classes }) => (
  <div className={classes.root}>
    {events.map((event, index) => {
      const { title, icon, variant } = getEventMeta(event);
      const changes = event.changes || {};
      const visibleChanges = Object.entries(changes).filter(([key]) => FIELD_LABELS[key]);
      const isCommentOnly = variant === 'comment';

      return (
        <div key={event.id} className={classes.item}>
          {index < events.length - 1 && <div className={classes.timelineLine} />}
          <div className={`${classes.dot} ${classes[`dot_${variant}`]}`}>
            {icon}
          </div>
          <div className={`${classes.card} ${classes[`card_${variant}`]}`}>
            <div className={classes.cardHeader}>
              <Text className={classes.cardTitle}>{title}</Text>
              <Text className={classes.cardDate}>{formatEventDate(event.created_at)}</Text>
            </div>
            <Text className={classes.author}>{event.whodunnit || 'Система'}</Text>
            {isCommentOnly ? (
              <Text className={classes.commentText}>{changes.comment}</Text>
            ) : (
              <div className={classes.changes}>
                {visibleChanges.map(([key, value]) => (
                  <div key={key} className={classes.changeRow}>
                    <Text className={classes.changeLabel}>{FIELD_LABELS[key]}</Text>
                    <Text className={classes.changeValue}>{formatFieldValue(key, value)}</Text>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    })}
  </div>
);

const styles = () => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '4px 0 8px',
    width: '100%',
  },
  item: {
    position: 'relative',
    display: 'flex',
    gap: '12px',
    paddingLeft: '4px',
  },
  timelineLine: {
    position: 'absolute',
    left: '17px',
    top: '36px',
    bottom: '-12px',
    width: '2px',
    backgroundColor: '#B88AD6',
  },
  dot: {
    width: '34px',
    height: '34px',
    minWidth: '34px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '15px',
    color: '#59059B',
    backgroundColor: '#FFFFFF',
    border: '2px solid #B88AD6',
    zIndex: 1,
  },
  dot_comment: {
    backgroundColor: '#E8F8EF',
    borderColor: '#52C41A',
    color: '#389E0D',
  },
  dot_create: {
    backgroundColor: '#F3EBFA',
    borderColor: '#8F00FF',
  },
  dot_update: {
    backgroundColor: '#FFFFFF',
  },
  card: {
    flex: 1,
    minWidth: 0,
    padding: '12px 14px',
    borderRadius: '18px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5D4F2',
    boxShadow: '0 2px 8px rgba(89, 5, 155, 0.06)',
  },
  card_comment: {
    backgroundColor: '#F6FFED',
    borderColor: '#B7EB8F',
  },
  card_create: {
    backgroundColor: '#FAF5FF',
    borderColor: '#D3ADF7',
  },
  card_update: {
    backgroundColor: '#FFFFFF',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '8px',
    marginBottom: '4px',
  },
  cardTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#59059B',
    lineHeight: '18px',
  },
  cardDate: {
    fontSize: '11px',
    color: '#8C8C8C',
    whiteSpace: 'nowrap',
    lineHeight: '18px',
  },
  author: {
    display: 'block',
    fontSize: '12px',
    color: '#595959',
    marginBottom: '8px',
  },
  commentText: {
    display: 'block',
    fontSize: '14px',
    color: '#262626',
    lineHeight: '20px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  changes: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  changeRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: '6px 8px',
    borderRadius: '10px',
    backgroundColor: 'rgba(205, 165, 230, 0.18)',
  },
  changeLabel: {
    fontSize: '11px',
    color: '#8C8C8C',
    lineHeight: '14px',
  },
  changeValue: {
    fontSize: '13px',
    color: '#262626',
    lineHeight: '18px',
    wordBreak: 'break-word',
  },
});

export default withStyles(styles)(ExpenseEventsList);
